import { useRef, useCallback } from 'react';
import type { FormBlockHandle } from '../../core/types';
import { BlockEventType } from '../../core/constants';
import { useEventScope } from '../useEventScope';
import { useBlockHandle } from '../useBlockHandle';

interface FormRendererHandleLike {
  submitForm: () => Promise<unknown>;
  getFormValues: () => Record<string, unknown>;
  triggerValidation: () => Promise<boolean>;
  isDirty: boolean;
  isSubmitting: boolean;
}

interface UseFormEventAdapterOptions {
  onExternalPatchValues?: (values: Record<string, unknown>) => void;
  onExternalReset?: (values?: Record<string, unknown>) => void;
}

export function useFormEventAdapter(
  semanticId: string | undefined,
  formHandle: FormRendererHandleLike | null,
  options?: UseFormEventAdapterOptions,
) {
  const scope = useEventScope();
  const formHandleRef = useRef(formHandle);
  formHandleRef.current = formHandle;

  const handleFactory = useCallback((): FormBlockHandle => {
    return {
      semanticId: semanticId!,
      blockType: 'form',
      capabilities: new Set([
        'submitForm',
        'patchValues',
        'resetForm',
      ]),
      submitForm: () => formHandleRef.current?.submitForm() ?? Promise.resolve(undefined),
      getFormValues: () => formHandleRef.current?.getFormValues() ?? {},
      triggerValidation: () => formHandleRef.current?.triggerValidation() ?? Promise.resolve(true),
      patchValues: (values) => options?.onExternalPatchValues?.(values),
      resetForm: (values) => options?.onExternalReset?.(values),
      get isDirty() { return formHandleRef.current?.isDirty ?? false; },
      get isSubmitting() { return formHandleRef.current?.isSubmitting ?? false; },
      dispose: () => {},
    };
  }, [semanticId, options]);

  useBlockHandle(semanticId, handleFactory, [formHandle, options]);

  const publishSubmitSuccess = useCallback(
    (result: Record<string, unknown>, values: Record<string, unknown>, mode: 'create' | 'edit') => {
      if (!scope || !semanticId) return;
      scope.bus.publish(BlockEventType.SUBMIT_SUCCESS, semanticId, { result, values, mode });
    },
    [scope, semanticId],
  );

  const publishSubmitFailure = useCallback(
    (errors: Array<{ fieldName: string; error: string }>, mode: 'create' | 'edit') => {
      if (!scope || !semanticId) return;
      scope.bus.publish(BlockEventType.SUBMIT_FAILURE, semanticId, { errors, mode });
    },
    [scope, semanticId],
  );

  const publishValuesChanged = useCallback(
    (changedFields: Record<string, unknown>, allValues: Record<string, unknown>) => {
      if (!scope || !semanticId) return;
      scope.bus.publish(BlockEventType.VALUES_CHANGED, semanticId, { changedFields, allValues });
    },
    [scope, semanticId],
  );

  const publishArrayChanged = useCallback(
    (arrayName: string, action: 'add' | 'remove' | 'reorder', index: number, count: number) => {
      if (!scope || !semanticId) return;
      scope.bus.publish(BlockEventType.ARRAY_CHANGED, semanticId, {
        arrayName,
        action,
        index,
        count,
      });
    },
    [scope, semanticId],
  );

  return {
    publishSubmitSuccess,
    publishSubmitFailure,
    publishValuesChanged,
    publishArrayChanged,
    scope,
  };
}
